import React, { useState, useEffect } from 'react';
import { Form as FinalForm } from 'react-final-form';
import css from './ListingPage.module.css';
import {
  ExternalLink,
  FieldSelectModern,
  FieldTextInput,
  IconSpinner,
  PrimaryButton,
} from '../../components';
import { useUserBalance } from '../../util/api';
import { estimatedTimes, serviceCategories } from '../../config/configListing';
import { CiCircleInfo } from 'react-icons/ci';
import { Tooltip } from 'react-tippy';
import {
  composeValidators,
  maxLength,
  maxValueValidation,
  minValueValidation,
  required,
} from '../../util/validators';
import { useIntl } from 'react-intl';
import CustomDatePicker from '../../components/CustomDatePicker/CustomDatePicker';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import FieldRange from '../../components/FieldRange/FieldRange';
import { formatBookingTime, formatDeadlineDate } from '../../util/dates';
const ProposalForm = props => {
  const history = useHistory();
  const handleSubmit = values => {
    const { onSendProposal, listing, authorId } = props;

    const { service: formService, estimate: formEstimate, deadLine, message, femCoins } = values;
    const bookingEnd = formatDeadlineDate(deadLine); // Set deadline to midnight EST
    const bookingStart = formatBookingTime(new Date()); // Start date and time with formatted minutes

    const service = formService?.value;
    const estimate = formEstimate?.value;
    const listingId = listing?.id?.uuid;
    const orderParams = {
      listingId,
      bookingStart,
      bookingEnd,
      service,
      estimate,
      message,
      femCoins,
      authorId,
    };
    onSendProposal(orderParams).then(res => {
      if (res) {
        const transaction = res?.data?.data;
        const txId = transaction?.id?.uuid;
        if (txId) {
          history.push(`/order/${txId}/details`);
        }
      }
    });
  };
  const isFemxProfile = props?.listing?.attributes?.publicData?.listingType === 'fem-x-profile';
  return (
    <FinalForm
      {...props}
      onSubmit={handleSubmit}
      initialValues={{
        message: isFemxProfile
          ? 'Hi! I’m excited to connect. I’d love for you to check out my proposal. Looking forward to the possibility of working together!'
          : 'Hi! I can help you out—I believe I’d be a great fit for this exchange and would love the opportunity to collaborate with you!',
      }}
      render={formRenderProps => {
        const {
          handleSubmit,
          values,
          form,
          author,
          listing,
          invalid,
          disabled,
          proposalInProgress,
          proposalError,
          currentUser,
          details,
          fetchDetailsInProgress,
          fetchDetailsError,
        } = formRenderProps;

        const { estimatedTimesData = [], servicesData = [] } = details || {};
        const currentUserId = currentUser?.id?.uuid;
        const [balance] = useUserBalance(currentUser?.id?.uuid);
        if (!currentUserId) {
          return null;
        }

        const [minPrice, setMinPrice] = useState({ min: 0, max: 0 });
        useEffect(() => {
          console.log({ values });
          if (values?.service?.value == 'flat_rate_or_custom_projects') {
            const { min, max } = values?.service || {};
            setMinPrice({ min, max});
          } else if (values.service && values.estimate) {
            const service = values.service;
            const { min: serviceMin, max: serviceMax } = service || {};
            const estimate = values.estimate;
            const { min: estimateMin, max: estimateMax } = estimate || {};
            const min = serviceMin * estimateMin;
            const max = serviceMax * estimateMax;
            setMinPrice({ min, max });
          }
        }, [values.service, values.estimate]);
        const { attributes } = currentUser || {};
        const { profile } = attributes || {};
        // Removed femx_coins read from metadata as we're using balance from API now
        const intl = useIntl();
        const displayName = author?.attributes?.profile?.displayName;
        const submitDisabled = invalid || disabled || proposalInProgress;
        if (fetchDetailsInProgress) {
          return (
            <div className={css.proposalForm}>
              <p>
                Loading... <IconSpinner />
              </p>
            </div>
          );
        }
        return (
          <form className={css.proposalForm} onSubmit={handleSubmit}>
            <h1 className={css.proposalFormTitle}>
              {intl.formatMessage(
                { id: 'ListingPage.ProposalForm.mainHeading' },
                { displayName: displayName }
              )}
              *
            </h1>
            {fetchDetailsError ? (
              <p className={css.error}>
                {fetchDetailsError?.message || 'Something went wrong. Please try again.'}
              </p>
            ) : null}
            <div className={css.proposalFormContent}>
              {isFemxProfile ? (
                <div>
                  <FieldSelectModern
                    className={css.field}
                    id="service"
                    name="service"
                    label={intl.formatMessage({ id: 'ListingPage.ProposalForm.femxServiceLabel' })}
                    placeholder={intl.formatMessage({
                      id: 'ListingPage.ProposalForm.femxServicePlaceholder',
                    })}
                    subText={intl.formatMessage(
                      {
                        id: 'ListingPage.ProposalForm.femxServiceSubText',
                      },
                      {
                        link: (
                          <ExternalLink href="/p/how-femcoin-offers-work" target="#">
                            Learn how FemCoin offers work
                          </ExternalLink>
                        ),
                      }
                    )}
                    options={servicesData}
                    // options={serviceCategories}
                    isSearchable={false}
                    onFieldChange={e => {
                      form.change('femCoins', undefined);
                    }}
                    tootlTip={true}
                    validate={required(
                      intl.formatMessage({ id: 'ListingPage.ProposalForm.femxRequiredMessage' })
                    )}
                  />
                  {values?.service?.value !== 'flat_rate_or_custom_projects' ? (
                    <FieldSelectModern
                      className={css.field}
                      id="estimate"
                      name="estimate"
                      isSearchable={false}
                      tootlTip={true}
                      label={intl.formatMessage({
                        id: 'ListingPage.ProposalForm.femxEstimateLabel',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'ListingPage.ProposalForm.femxEstimatePlaceholder',
                      })}
                      subText={intl.formatMessage(
                        { id: 'ListingPage.ProposalForm.femxEstimatesubText' },
                        {
                          link: (
                            <ExternalLink href="/p/how-femcoin-offers-work" target="#">
                              Learn how FemCoin ranges are calculated
                            </ExternalLink>
                          ),
                        }
                      )}
                      options={estimatedTimesData}
                      // options={estimatedTimes}
                      validate={
                        values?.service?.value !== 'flat_rate_or_custom_projects'
                          ? required(
                              intl.formatMessage({
                                id: 'ListingPage.ProposalForm.femxEstimateRequiredMessage',
                              })
                            )
                          : null
                      }
                    />
                  ) : null}

                  {minPrice?.max && minPrice?.min ? (
                    <FieldRange
                      className={css.field}
                      id="femCoins"
                      name="femCoins"
                      min={minPrice?.min}
                      max={minPrice?.max}
                      label={intl.formatMessage({ id: 'ListingPage.ProposalForm.femxFemCoins' })}
                      subText={intl.formatMessage({
                        id: 'ListingPage.ProposalForm.femcoinsSubtitle',
                      })}
                      placeholder={intl.formatMessage({
                        id: 'ListingPage.ProposalForm.femxFemcoinsPlaceholder',
                      })}
                      validate={composeValidators(
                        required(
                          intl.formatMessage({
                            id: 'ListingPage.ProposalForm.femxFemcoinsRequired',
                          })
                        ),
                        maxValueValidation(
                          intl.formatMessage({
                            id: 'ListingPage.ProposalForm.femxFemcoinsUserValidation',
                          }),
                          balance
                        ),

                        maxValueValidation(
                          intl.formatMessage({
                            id: 'ListingPage.ProposalForm.femxCoinsMaxRequired',
                          }),
                          minPrice?.max
                        ),

                        minValueValidation(
                          intl.formatMessage({ id: 'ListingPage.ProposalForm.femxMinRequired' }),

                          minPrice?.min
                        )
                      )}
                    />
                  ) : null}
                  {/* <FieldTextInput
                className={css.fieldHide}
                id="femCoins"
                name="femCoins"
                type="number"
                label="FemCoins"
                placeholder="Enter a number of FemCoins."
                validate={composeValidators(
                  required('Please enter a number of FemCoins.'),
                  maxValueValidation(
                    'Sorry, you do not have enough FemCoins in your bank',
                    balance
                  ),

                  maxValueValidation(
                    'This offer is above typical rates for this category. Consider adjusting for fairness.',
                    minPrice?.max
                  ),

                  minValueValidation(
                    'This offer is below typical rates for this category. Consider adjusting for fairness.',

                    minPrice?.min
                  )
                )}
              /> */}
                  {minPrice?.max && minPrice?.min ? (
                    <p className={css.priceRange}>
                      {intl.formatMessage({ id: 'ListingPage.ProposalForm.autoSuggestedRange' })}
                      <span>
                        {''} {minPrice?.min} - {minPrice?.max}
                      </span>{' '}
                      {intl.formatMessage({ id: 'ListingPage.ProposalForm.fc' })}{' '}
                      <Tooltip
                        key={'femxoin'}
                        animation={false}
                        duration={0}
                        interactive
                        html={
                          <div className={css.toolTipParent}>
                            {intl.formatMessage({
                              id: 'ListingPage.ProposalForm.autoGeneratedTip',
                            })}
                          </div>
                        }
                        className={css.ToolTip}
                        position="bottom"
                      >
                        <span className={css.seeMore}>
                          <CiCircleInfo className={css.info} />
                        </span>
                      </Tooltip>
                    </p>
                  ) : null}
                  <CustomDatePicker
                    className={css.field}
                    id="deadLine"
                    name="deadLine"
                    minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                    label={intl.formatMessage({ id: 'ListingPage.ProposalForm.femxDeadlineLabel' })}
                    placeholder={intl.formatMessage({
                      id: 'ListingPage.ProposalForm.femxDeadlineplaceholder',
                    })}
                    subText={intl.formatMessage({
                      id: 'ListingPage.ProposalForm.femxDeadlineSubtext',
                    })}
                    validate={required(
                      intl.formatMessage({ id: 'ListingPage.ProposalForm.femxDeadLineRequired' })
                    )}
                  />
                  <FieldTextInput
                    className={css.field}
                    type="textarea"
                    id="message"
                    name="message"
                    label={intl.formatMessage(
                      { id: 'ListingPage.ProposalForm.sendMessageLabel' },
                      { displayName: displayName }
                    )}
                    subText={intl.formatMessage({ id: 'ListingPage.ProposalForm.messageSubText' })}
                    validate={composeValidators(
                      required(
                        intl.formatMessage({ id: 'ListingPage.ProposalForm.messageRequiredText' })
                      ),
                      maxLength(
                        intl.formatMessage(
                          { id: 'ProfileSettingsForm.ProfileSettingsForm.maxLengt' },
                          { maxLength: 500 }
                        ),
                        500
                      )
                    )}
                  />
                </div>
              ) : (
                <div>
                  {/* <CustomDatePicker
                    className={css.field}
                    id="deadLine"
                    name="deadLine"
                    minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                    label="When could you complete this request by?"
                    placeholder="Select a deadline."
                    subText="Set the date for when you could complete this request by. FemCoins will transfer automatically on this date unless the exchange is marked complete earlier or flagged."
                    validate={required(
                      'A deadline is required to ensure smooth FemCoin transfers – please select a date.'
                    )}
                  />
                  <FieldTextInput
                    className={css.field}
                    type="textarea"
                    id="message"
                    name="message"
                    label={`Send a Message to ${displayName}`}
                    subText="IIntroduce yourself and share why you're the right fit for this exchange."
                    validate={composeValidators(
                      required('Your request needs context – share some details.'),
                      maxLength(
                        intl.formatMessage(
                          { id: 'ProfileSettingsForm.ProfileSettingsForm.maxLengt' },
                          { maxLength: 500 }
                        ),
                        500
                      )
                    )}
                  /> */}
                  <CustomDatePicker
                    className={css.field}
                    id="deadLine"
                    name="deadLine"
                    minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
                    label={intl.formatMessage({
                      id: 'ListingPage.ProposalForm.deadLineLabelExchange',
                    })}
                    placeholder={intl.formatMessage({
                      id: 'ListingPage.ProposalForm.femxDeadlineplaceholder',
                    })}
                    subText={intl.formatMessage({
                      id: 'ListingPage.ProposalForm.deadLineSubtextExchange',
                    })}
                    validate={required(
                      intl.formatMessage({ id: 'ListingPage.ProposalForm.femxDeadLineRequired' })
                    )}
                  />
                  <FieldTextInput
                    className={css.field}
                    type="textarea"
                    id="message"
                    name="message"
                    label={intl.formatMessage(
                      { id: 'ListingPage.ProposalForm.sendMessageLabel' },
                      { displayName: displayName }
                    )}
                    subText={intl.formatMessage({
                      id: 'ListingPage.ProposalForm.messageSubTextExchange',
                    })}
                    validate={composeValidators(
                      required(
                        intl.formatMessage({ id: 'ListingPage.ProposalForm.messageRequiredText' })
                      ),
                      maxLength(
                        intl.formatMessage(
                          { id: 'ProfileSettingsForm.ProfileSettingsForm.maxLengt' },
                          { maxLength: 500 }
                        ),
                        500
                      )
                    )}
                  />
                </div>
              )}
              {proposalError ? (
                <p className={css.error}>
                  {proposalError?.message || 'Something went wrong. Please try again.'}
                </p>
              ) : null}

              <PrimaryButton
                type="submit"
                className={css.proposalSubmitButton}
                disabled={submitDisabled}
                inProgress={proposalInProgress}
              >
                {intl.formatMessage({ id: 'ListingPage.ProposalForm.submitButtonLabel' })}
              </PrimaryButton>
              <p className={css.proposalTerms}>
                {intl.formatMessage({ id: 'ListingPage.ProposalForm.belowButtonNote' })}
              </p>
            </div>
          </form>
        );
      }}
    />
  );
};

export default ProposalForm;
